// ============================================================
// xScan by XCloud Solutions - Landing Page (i18n EN/ES)
// Part of xTrust by xCloud Solutions Suite — DETECT layer
// Per ADR-FRAMEWORK-028 v1.1 (in baseline repo)
// ============================================================
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useLocale } from '../i18n/useLocale';
import {
  Shield, Scan, FileText, Lock, Globe, Server, Zap, CheckCircle,
  ArrowRight, Eye, Code, Wifi, Terminal,
  ShieldCheck, BarChart3, Users, AlertTriangle, Languages
} from 'lucide-react';

const TOOLS = ['Nmap', 'Nuclei', 'OWASP ZAP', 'SSLyze', 'Subfinder', 'httpx', 'testssl.sh', 'Amass', 'Nikto', 'WPScan', 'crt.sh', 'interactsh'];

export default function LandingPage() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [quickTarget, setQuickTarget] = useState('');
  const { locale, t, toggleLocale } = useLocale();

  const handleQuickScan = (e: React.FormEvent) => {
    e.preventDefault();
    if (isAuthenticated) {
      navigate(`/app/scans/new?target=${encodeURIComponent(quickTarget)}`);
    } else {
      navigate(`/register?target=${encodeURIComponent(quickTarget)}`);
    }
  };

  const SCAN_FEATURES = [
    { icon: Shield, title: 'Pentesting con Evidencia', desc: 'Pruebas de explotabilidad reales — no solo deteccion, sino demostracion de impacto', color: 'from-red-500 to-red-600' },
    { icon: Eye, title: 'Dark Web Intelligence', desc: 'Busca credenciales expuestas y filtraciones de datos de su organizacion', color: 'from-purple-500 to-purple-600' },
    { icon: Code, title: 'API Security (OWASP Top 10)', desc: 'Testing completo de APIs REST contra las 10 vulnerabilidades mas criticas', color: 'from-blue-500 to-blue-600' },
    { icon: Globe, title: 'Surface Discovery', desc: 'Descubre GitHub repos, S3 buckets, email leaks, Shodan, WHOIS automaticamente', color: 'from-emerald-500 to-green-600' },
    { icon: Server, title: 'Red Interna + Cloud', desc: 'Agente on-prem para red interna + postura AWS/Azure con CIS Benchmarks', color: 'from-orange-400 to-orange-500' },
    { icon: Wifi, title: 'BAS — MITRE ATT&CK', desc: 'Simulacion de ataques para validar que sus controles realmente detectan amenazas', color: 'from-pink-500 to-rose-500' },
    { icon: Lock, title: 'AI Autonomous Scanning', desc: 'Crawler inteligente + coordinador AI que razona y adapta ataques en tiempo real', color: 'from-violet-500 to-violet-600' },
    { icon: Terminal, title: 'SIEM + Compliance', desc: 'Export a Splunk/ELK/Wazuh/SARIF + mapeo PCI-DSS, ISO 27001, NIST, OWASP', color: 'from-gray-600 to-gray-700' },
  ];

  const otherLocaleLabel = locale === 'es' ? 'EN' : 'ES';

  return (
    <div className="min-h-screen bg-white">
      {/* Navbar */}
      <nav className="fixed top-0 z-50 w-full border-b border-gray-200 bg-white/95 backdrop-blur-sm">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-purple-600 to-purple-700 shadow-lg shadow-purple-500/20">
              <Shield className="h-5 w-5 text-white" />
            </div>
            <div className="flex flex-col">
              <span className="text-lg font-bold text-purple-900 leading-tight">xScan</span>
              <span className="text-[9px] font-medium text-gray-500 uppercase tracking-widest leading-tight">by XCloud Solutions</span>
            </div>
          </Link>
          <div className="flex items-center gap-3 sm:gap-4">
            {/* Language switcher */}
            <button
              type="button"
              onClick={toggleLocale}
              className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2 py-1 text-xs font-semibold text-gray-600 hover:border-purple-500 hover:text-purple-700 transition-colors"
              title={locale === 'es' ? 'Switch to English' : 'Cambiar a Español'}
              aria-label={locale === 'es' ? 'Switch to English' : 'Cambiar a Español'}
            >
              <Languages className="h-3.5 w-3.5" />
              <span>{otherLocaleLabel}</span>
            </button>
            {isAuthenticated ? (
              <Link to="/app" className="btn-primary text-sm">
                <BarChart3 className="h-4 w-4" /> {t('nav.dashboard')}
              </Link>
            ) : (
              <>
                <Link to="/login" className="hidden sm:inline text-sm font-medium text-gray-600 hover:text-purple-700 transition-colors">
                  {t('nav.login')}
                </Link>
                <Link to="/register" className="btn-primary text-sm">
                  {t('nav.register')} <ArrowRight className="h-4 w-4" />
                </Link>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden pt-16">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-50 via-white to-orange-50" />
        <div className="absolute -top-40 right-0 h-[500px] w-[500px] rounded-full bg-purple-200/30 blur-[120px]" />
        <div className="absolute bottom-0 left-0 h-[300px] w-[400px] rounded-full bg-orange-200/30 blur-[100px]" />

        <div className="relative mx-auto max-w-7xl px-4 pt-20 pb-24 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-purple-200 bg-purple-50 px-4 py-1.5 text-sm font-medium text-purple-700">
              <Zap className="h-4 w-4 text-orange-500" />
              {t('hero.badge')}
            </div>

            {/* xTrust portfolio badge — visible on hero */}
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-purple-200/40 bg-white/60 backdrop-blur-sm px-3 py-1 text-xs font-medium text-gray-600">
              <span className="text-[0.6rem] uppercase tracking-widest text-gray-400">{t('xtrust.partof')}</span>
              <span className="font-bold text-purple-900">xTrust</span>
              <span className="text-gray-400">by xCloud Solutions</span>
              <span className="text-gray-300">·</span>
              <span className="font-medium text-purple-700">DETECT layer</span>
            </div>

            <h1 className="mx-auto max-w-4xl text-4xl font-extrabold tracking-tight text-gray-900 sm:text-5xl lg:text-6xl">
              {t('hero.title.line1')}{' '}
              <span className="bg-gradient-to-r from-purple-600 to-purple-800 bg-clip-text text-transparent">
                {t('hero.title.gradient')}
              </span>{' '}
              {t('hero.title.line2')}
            </h1>

            <p className="mx-auto mt-6 max-w-2xl text-lg text-gray-600">
              {t('hero.subtitle')}
            </p>

            {/* Quick scan form */}
            <form onSubmit={handleQuickScan} className="mx-auto mt-10 max-w-xl">
              <div className="flex gap-2 rounded-2xl border border-gray-300 bg-white p-2 shadow-xl shadow-gray-200/50">
                <div className="relative flex-1">
                  <Globe className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={quickTarget}
                    onChange={(e) => setQuickTarget(e.target.value)}
                    placeholder={t('hero.input.placeholder')}
                    className="w-full rounded-xl bg-transparent py-3.5 pl-12 pr-4 text-gray-800 placeholder-gray-400 focus:outline-none"
                  />
                </div>
                <button type="submit" className="btn-primary whitespace-nowrap rounded-xl px-6">
                  <Scan className="h-5 w-5" />
                  {t('hero.cta.scan')}
                </button>
              </div>
              <p className="mt-3 text-xs text-gray-400">
                {t('hero.disclaimer')}
              </p>
            </form>

            {/* Stats */}
            <div className="mx-auto mt-16 grid max-w-3xl grid-cols-3 gap-8">
              {[
                { label: 'Modulos de Seguridad', value: '34' },
                { label: 'Frameworks Evaluados', value: '5' },
                { label: 'Formatos de Export', value: '6' },
              ].map((stat) => (
                <div key={stat.label} className="text-center">
                  <p className="text-3xl font-bold text-purple-900">{stat.value}</p>
                  <p className="mt-1 text-sm text-gray-500">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="relative py-24 bg-gray-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-sm font-semibold text-orange-500 uppercase tracking-wider">{t('features.eyebrow')}</p>
            <h2 className="mt-2 text-3xl font-bold text-gray-900 sm:text-4xl">
              {t('features.title')}
            </h2>
            <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
              {t('features.subtitle')}
            </p>
          </div>

          <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {SCAN_FEATURES.map((f) => (
              <div key={f.title} className="group rounded-2xl bg-white border border-gray-200 p-6 shadow-sm transition-all duration-300 hover:shadow-lg hover:border-purple-200 hover:-translate-y-1">
                <div className={`mb-4 inline-flex rounded-xl bg-gradient-to-br ${f.color} p-3 text-white shadow-lg`}>
                  <f.icon className="h-6 w-6" />
                </div>
                <h3 className="text-base font-semibold text-gray-900">{f.title}</h3>
                <p className="mt-2 text-sm text-gray-500">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="relative py-24 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-sm font-semibold text-orange-500 uppercase tracking-wider">{t('how.eyebrow')}</p>
            <h2 className="mt-2 text-3xl font-bold text-gray-900 sm:text-4xl">{t('how.title')}</h2>
          </div>

          <div className="mt-16 grid gap-12 md:grid-cols-3">
            {[
              { step: '01', icon: Users, titleKey: 'how.step1.title', descKey: 'how.step1.desc', color: 'from-purple-600 to-purple-700' },
              { step: '02', icon: Scan, titleKey: 'how.step2.title', descKey: 'how.step2.desc', color: 'from-orange-400 to-orange-500' },
              { step: '03', icon: FileText, titleKey: 'how.step3.title', descKey: 'how.step3.desc', color: 'from-pink-500 to-rose-500' },
            ].map((item) => (
              <div key={item.step} className="relative text-center">
                <div className={`mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br ${item.color} text-white shadow-xl`}>
                  <item.icon className="h-7 w-7" />
                </div>
                <span className="absolute -top-2 left-1/2 -translate-x-1/2 rounded-full bg-purple-900 px-3 py-0.5 text-[10px] font-bold text-white tracking-wider">
                  {t('how.step.label')} {item.step}
                </span>
                <h3 className="text-xl font-semibold text-gray-900">{t(item.titleKey)}</h3>
                <p className="mt-3 text-gray-500">{t(item.descKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What You Get — Use Cases */}
      <section className="relative py-24 bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-sm font-semibold text-orange-500 uppercase tracking-wider">{t('whatuget.eyebrow')}</p>
            <h2 className="mt-2 text-3xl font-bold text-gray-900 sm:text-4xl">
              {t('whatuget.title')}
            </h2>
            <p className="mt-4 text-lg text-gray-600 max-w-3xl mx-auto">
              {t('whatuget.subtitle')}
            </p>
          </div>

          <div className="mt-16 grid gap-8 md:grid-cols-3">
            {/* Card 1: Proof of Exploitation */}
            <div className="rounded-2xl bg-white border border-red-200 p-8 shadow-sm hover:shadow-lg transition-all">
              <div className="mb-4 inline-flex rounded-xl bg-gradient-to-br from-red-500 to-red-600 p-3 text-white shadow-lg">
                <AlertTriangle className="h-6 w-6" />
              </div>
              <h3 className="text-xl font-bold text-gray-900">Evidencia de Explotabilidad</h3>
              <p className="mt-3 text-gray-500">No reportamos teorias. Demostramos impacto real con dark web intelligence, API security y explotacion segura.</p>
              <ul className="mt-4 space-y-2 text-sm text-gray-600">
                <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-red-500 flex-shrink-0" /> SQLi, LFI, credenciales extraidas + dark web leaks</li>
                <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-red-500 flex-shrink-0" /> OWASP API Top 10 testing completo</li>
                <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-red-500 flex-shrink-0" /> Attack chains + grafo de ataque visual</li>
              </ul>
            </div>

            {/* Card 2: NIST + Compliance */}
            <div className="rounded-2xl bg-white border border-blue-200 p-8 shadow-sm hover:shadow-lg transition-all">
              <div className="mb-4 inline-flex rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 p-3 text-white shadow-lg">
                <Shield className="h-6 w-6" />
              </div>
              <h3 className="text-xl font-bold text-gray-900">Madurez NIST CSF 2.0</h3>
              <p className="mt-3 text-gray-500">Evaluacion automatica de madurez contra 5 frameworks con executive cockpit en tiempo real.</p>
              <ul className="mt-4 space-y-2 text-sm text-gray-600">
                <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-blue-500 flex-shrink-0" /> NIST CSF 2.0, PCI-DSS, ISO 27001, SOC 2, OWASP</li>
                <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-blue-500 flex-shrink-0" /> Executive Security Cockpit + video report</li>
                <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-blue-500 flex-shrink-0" /> BAS MITRE ATT&CK validation + threat intel feed</li>
              </ul>
            </div>

            {/* Card 3: Financial Risk */}
            <div className="rounded-2xl bg-white border border-green-200 p-8 shadow-sm hover:shadow-lg transition-all">
              <div className="mb-4 inline-flex rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 p-3 text-white shadow-lg">
                <BarChart3 className="h-6 w-6" />
              </div>
              <h3 className="text-xl font-bold text-gray-900">Riesgo en Dolares</h3>
              <p className="mt-3 text-gray-500">Cuantificacion financiera del riesgo cyber con AI analyst (AURA) para decisiones de negocio.</p>
              <ul className="mt-4 space-y-2 text-sm text-gray-600">
                <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" /> CRQ: perdida esperada anualizada por vulnerabilidad</li>
                <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" /> AI Analyst AURA con recomendaciones priorizadas</li>
                <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" /> Benchmark industria + cloud security posture</li>
              </ul>
            </div>
          </div>

          {/* Use cases row */}
          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { icon: ShieldCheck, title: 'Para su Equipo Tecnico', desc: 'Hallazgos con scripts de reproduccion, remediation-as-code, one-click retest, API security testing.' },
              { icon: Users, title: 'Para su Junta Directiva', desc: 'Security Cockpit en tiempo real, video resumen ejecutivo, riesgo en dolares, benchmark industria.' },
              { icon: FileText, title: 'Para su Aseguradora', desc: 'Certificado NIST CSF 2.0, CRQ, compliance mapping 5 frameworks, reporte para poliza.' },
              { icon: Lock, title: 'Para sus Auditores', desc: 'SIEM export (Splunk/ELK/Wazuh/SARIF), BAS MITRE ATT&CK, dark web intel, cloud posture.' },
            ].map((uc) => (
              <div key={uc.title} className="rounded-xl bg-white border border-gray-200 p-5 hover:border-purple-200 hover:shadow-md transition-all">
                <uc.icon className="h-6 w-6 text-purple-600 mb-2" />
                <h4 className="font-bold text-gray-900 text-sm">{uc.title}</h4>
                <p className="mt-1 text-xs text-gray-500">{uc.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What makes us different */}
      <section className="relative py-20 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-sm font-semibold text-purple-600 uppercase tracking-wider">Por que xFreeScan</p>
            <h2 className="mt-2 text-3xl font-bold text-gray-900 sm:text-4xl">Lo que Otros Necesitan 7 Herramientas, Nosotros lo Hacemos en Una</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {[
              { label: 'Pentesting + Evidencia', replaces: 'Nuclei + Metasploit' },
              { label: 'Compliance Mapping', replaces: 'Drata + Vanta' },
              { label: 'Risk Quantification', replaces: 'RiskLens ($30K+)' },
              { label: 'Dark Web Intel', replaces: 'SpiderFoot + HIBP' },
              { label: 'SIEM Export', replaces: 'Integraciones custom' },
              { label: 'AI Analyst', replaces: 'Consultor senior ($$$)' },
            ].map((d, i) => (
              <div key={i} className="text-center p-4 rounded-xl border border-purple-100 bg-purple-50/50">
                <p className="font-bold text-sm text-purple-900">{d.label}</p>
                <p className="text-[10px] text-gray-500 mt-1 line-through">{d.replaces}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Open Source Tools */}
      <section className="relative py-20 bg-gradient-to-br from-purple-900 via-purple-800 to-purple-900">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wMyI+PHBhdGggZD0iTTM2IDE4YzMuMzE0IDAgNiAyLjY4NiA2IDZzLTIuNjg2IDYtNiA2LTYtMi42ODYtNi02IDIuNjg2LTYgNi02eiIvPjwvZz48L2c+PC9zdmc+')] opacity-100" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm font-semibold text-orange-400 uppercase tracking-wider">{t('tools.eyebrow')}</p>
          <h2 className="mt-2 text-3xl font-bold text-white sm:text-4xl">
            {t('tools.title')}
          </h2>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            {TOOLS.map((tool) => (
              <span key={tool} className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-sm font-medium text-white backdrop-blur-sm">
                <Terminal className="h-3.5 w-3.5 text-orange-400" />
                {tool}
              </span>
            ))}
          </div>
          <p className="mt-6 text-xs text-white/60 max-w-2xl mx-auto">
            {t('tools.note')}
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 bg-gradient-to-br from-orange-50 via-white to-purple-50">
        <div className="relative mx-auto max-w-4xl px-4 text-center sm:px-6">
          <div className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-purple-600 to-purple-700 text-white shadow-xl shadow-purple-500/25 mb-6">
            <ShieldCheck className="h-8 w-8" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 sm:text-4xl">
            {t('cta.title')}
          </h2>
          <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
            {t('cta.subtitle')}
          </p>
          <div className="mt-8 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
            <Link to="/register" className="btn-primary text-base px-8 py-4">
              {t('cta.start')} <ArrowRight className="h-5 w-5" />
            </Link>
            <a href="https://www.xclos.com" target="_blank" rel="noopener noreferrer" className="btn-secondary text-base px-8 py-4">
              {t('cta.knowxclos')}
            </a>
          </div>
          <p className="mt-6 text-sm text-gray-400">
            {t('cta.remediation')}{' '}
            <a href="https://www.xclos.com" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:text-purple-500 font-medium">
              {t('cta.contactus')}
            </a>
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-200 bg-white py-10">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {/* Part of xTrust banner — 4 products (Pirca added per ADR-028 v1.1) */}
          <div className="mb-6 pb-6 border-b border-gray-100">
            <div className="flex flex-col items-center justify-between gap-3 sm:flex-row">
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <span className="text-[0.65rem] uppercase tracking-widest text-gray-400">{t('xtrust.partof')}</span>
                <span className="font-semibold text-purple-900">xTrust</span>
                <span className="text-gray-400">by xCloud Solutions</span>
              </div>
              <div className="flex flex-wrap items-center justify-center gap-3 text-xs text-gray-500">
                <span className="text-gray-400">{t('xtrust.suite')}:</span>
                <span className="font-medium text-purple-700">xScan · DETECT ✓</span>
                <span className="text-gray-300">·</span>
                <a href="https://ossfia.ai" target="_blank" rel="noopener noreferrer" className="hover:text-purple-600 transition-colors">OSSFIA · PREVENT</a>
                <span className="text-gray-300">·</span>
                <span className="text-gray-500">GlassPlane · MONITOR</span>
                <span className="text-gray-300">·</span>
                <a href="https://pirca.xclos.com" target="_blank" rel="noopener noreferrer" className="font-medium text-rose-600 hover:text-rose-700 transition-colors">Pirca · SOVEREIGN</a>
              </div>
            </div>
          </div>
          <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
            <div className="flex items-center gap-2.5">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-purple-600 to-purple-700">
                <Shield className="h-4 w-4 text-white" />
              </div>
              <span className="font-semibold text-purple-900">xScan</span>
              <span className="text-xs text-gray-400">by XCloud Solutions</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-gray-500">
              <a href="https://www.xclos.com" target="_blank" rel="noopener noreferrer" className="hover:text-purple-600 transition-colors">xclos.com</a>
              <span>&copy; {new Date().getFullYear()} XCloud Solutions</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
